import { ClassNames, useTheme } from '@emotion/react'
import facepaint from 'facepaint'
import isMobile from 'ismobilejs'
import { FC, useEffect, useRef } from 'react'
import { usePlayerState } from '../contexts/playerState'
import { formatTime } from '../utils'
import { optimizeControlBarSpace } from '../utils/controlBar'
import { mqs } from '../utils/theme'

const isMobileDevice = isMobile().phone

interface Props {
  showSeekbar: boolean
  showTimers: boolean
  showLiveUI: boolean
}

const CustomControls: FC<Props> = ({
  showTimers,
  showLiveUI,
}) => {
  const {
    isInitialized,
    playerInstance: player,
    isAtLiveEdge,
    isLive,
    totalTime,
    playerWidth,
    playerHeight,
  } = usePlayerState()

  const oldClassesRef = useRef({
    bigPlayButton: '',
    loadingSpinner: '',
    livePill: '',
    playToggle: '',
    player: '',
    seekToLive: '',
  }) // When recompiling emotion generated classes they can change based on props (eg. player size) and they need to be removed from each previous update

  const theme = useTheme()
  const controlBar = player?.controlBar?.el()

  useEffect(() => {
    const timeRef = setTimeout(() => {
      if (playerWidth > 0 && controlBar?.scrollWidth && player?.controlBar) {
        optimizeControlBarSpace(
          player.controlBar,
          isMobileDevice,
          isAtLiveEdge || false,
          isLive || false,
          totalTime > 0,
        )
      }
    }, 0)

    return () => {
      timeRef && clearTimeout(timeRef)
    }
  }, [
    playerWidth,
    controlBar,
    player?.controlBar,
    controlBar?.scrollWidth,
    controlBar?.clientWidth,
    isLive,
    isAtLiveEdge,
    totalTime,
  ])

  if (!isInitialized || !player?.el()) return null

  const {
    colors: { primary: color },
  } = theme

  return (
    <ClassNames>
      {({ css }) => {
        const bigbigPlayButtonStyleClass = css(bigPlayButtonStyle(color))
        const spinnerStyleClass = css(spinnerStyle(color))
        const playButtonStyleClass = css(
          playButtonStyle(playerWidth, playerHeight, color),
        )
        const playerStyleClass = css(playerStyle)

        oldClassesRef.current.player = playerStyleClass

        if (showTimers) {
          ;(player.controlBar as any).durationDisplay.el().innerText =
            formatTime(totalTime)
        }

        // Overwrite the default videojs component styling
        oldClassesRef.current.bigPlayButton &&
          player.bigPlayButton.removeClass(oldClassesRef.current.bigPlayButton)
        player.bigPlayButton.addClass(bigbigPlayButtonStyleClass)
        oldClassesRef.current.bigPlayButton = playButtonStyleClass

        oldClassesRef.current.loadingSpinner &&
          player.loadingSpinner.removeClass(
            oldClassesRef.current.loadingSpinner,
          )
        player.loadingSpinner.addClass(spinnerStyleClass)
        oldClassesRef.current.loadingSpinner = spinnerStyleClass

        oldClassesRef.current.playToggle &&
          (player.controlBar as any).playToggle.removeClass(
            oldClassesRef.current.playToggle,
          )
        oldClassesRef.current.playToggle &&
          (player.controlBar as any).playToggle.addClass(playButtonStyleClass)

        oldClassesRef.current.playToggle = playButtonStyleClass

        if (showLiveUI) {
          const controlBarLivePillStyleClass = css(
            controlBarStyle(color, isAtLiveEdge || false),
          )

          oldClassesRef.current.livePill &&
            player.controlBar.removeClass(oldClassesRef.current.livePill)
          player.controlBar.addClass(controlBarLivePillStyleClass)

          if (isLive) {
            const className = css(seekToLiveStyle(isAtLiveEdge || false))

            oldClassesRef.current.seekToLive &&
              (player?.controlBar as any)?.seekToLive?.removeClass(
                oldClassesRef.current.seekToLive,
              )
            oldClassesRef.current.seekToLive &&
              (player?.controlBar as any)?.seekToLive?.addClass(className)
            oldClassesRef.current.seekToLive = className
          } else {
            oldClassesRef.current.seekToLive &&
              (player?.controlBar as any)?.seekToLive?.removeClass(
                oldClassesRef.current.seekToLive,
              )
          }

          oldClassesRef.current.livePill = controlBarLivePillStyleClass
        }

        oldClassesRef.current.player &&
          player.removeClass(oldClassesRef.current.player)
        player.addClass(playerStyleClass)
        oldClassesRef.current.player = playerStyleClass

        return null
      }}
    </ClassNames>
  )
}

export default CustomControls

const mq = facepaint([mqs.m])
const zIndex = 30

const bigPlayButtonStyle = (color: string) => ({
  '&&&': {
    backgroundColor: 'transparent!important',
    border: 'transparent!important',
    boxShadow: 'none',
    margin: 0,
    transform: 'translate(-50%, -50%)',
    outline: 'none',
    '&:hover': {
      background: 'none',
    },
    // Solid triangle play button
    '&.vjs-big-play-button > .vjs-icon-placeholder': {
      position: 'absolute' as const,
      display: 'block',
      content: "''",
      boxSizing: 'border-box' as const,
      borderColor: `transparent transparent transparent ${color}`,
      borderStyle: 'solid',
      borderWidth: '26px 0 26px 40px',
      '&:before': {
        content: 'none',
      },
      '&:after': {
        content: 'none',
      },
    },
  },
})

const spinnerStyle = (color: string) =>
  mq({
    border: '0',
    margin: '0',
    transform: 'translate(-50%, -50%)',
    opacity: 1,
    zIndex: 1,
    '&::before': {
      position: 'absolute' as const,
      width: '50px',
      height: '50px',
      border: `6px solid ${color}`,
      borderTopColor: 'rgba(0, 0, 0, 0)!important',
      borderRadius: '50%',
      margin: 0,
      boxShadow: isMobileDevice ? 'unset' : '0 2px 4px rgba(0,0,0,0.5)',
    },
    '&::after': {
      content: 'none',
    },
  })

export const playButtonSize = '5em'

const playButtonStyle = (
  playerWidth: number,
  playerHeight: number,
  color: string,
) =>
  mq(
    isMobileDevice
      ? {
          '&&.vjs-button.vjs-control': {
            color,
            position: 'absolute' as const,
            height: `${playButtonSize}!important`,
            width: playButtonSize,
            zIndex,
            bottom: playerHeight / 2,
            left: playerWidth / 2,
            outline: '0',
            transform: 'translate(-50%, 50%)',
            transition: 'opacity 0.3s ease-in-out',
            '&.vjs-button > .vjs-icon-placeholder::before': {
              fontSize: playButtonSize,
              lineHeight: 'normal',
              textShadow: isMobileDevice
                ? 'unset'
                : '0 2px 4px rgba(0,0,0,0.5)',
            },
          },
        }
      : {},
  )

const playerStyle = isMobileDevice
  ? {
      '&.vjs-seeking .vjs-play-control': {
        opacity: 0,
      },
      '&.vjs-waiting .vjs-play-control': {
        opacity: 0,
      },
    }
  : {}

const controlBarStyle = (primaryColor: string, atLiveEdge: boolean) =>
  mq({
    height: '80px',
    background: 'none!important',
    fontSize: '1.2em',
    '.vjs-button.vjs-control': {
      width: ['3em', '4em'],
    },
    '.vjs-current-time': {
      order: 1,
      display: 'block',
      fontSize: '1.2em',
    },
    '.vjs-time-divider': {
      order: 2,
      display: atLiveEdge ? 'none' : 'block',
      fontSize: '1.2em',
      height: '40px',
      padding: 0,
      minWidth: 'unset',
    },
    '.vjs-duration': {
      order: 3,
      display: atLiveEdge ? 'none' : 'block',
      fontSize: '1.2em',
    },
    '.vjs-remaining-time': {
      display: 'none',
      fontSize: '1.2em',
    },
    '&&& .vjs-progress-holder': {
      height: '4px',
      boxShadow: isMobileDevice ? 'unset' : '0 2px 4px rgba(0,0,0,0.5)',
      '.vjs-play-progress': {
        backgroundColor: primaryColor,
        '&::before': {
          top: '-4px',
          boxShadow: isMobileDevice ? 'unset' : '0 2px 4px rgba(0,0,0,0.5)',
        },
      },
    },
    '&&& .vjs-volume-panel.vjs-lock': {
      width: 'auto',
    },
    '&&& .vjs-volume-panel .vjs-volume-bar': {
      margin: '1.6em 0.45em',
      boxShadow: isMobileDevice ? 'unset' : '0 2px 4px rgba(0,0,0,0.5)',
      '.vjs-volume-level': {
        backgroundColor: primaryColor,
        '&:before': {
          textShadow: isMobileDevice ? 'unset' : '0 2px 4px rgba(0,0,0,0.5)',
          top: '-3px',
        },
      },
    },
    '.vjs-button > .vjs-icon-placeholder::before': {
      textShadow: isMobileDevice ? 'unset' : '0 2px 4px rgba(0,0,0,0.5)',
    },
    '.vjs-button > .vjs-icon-placeholder': {
      textShadow: isMobileDevice ? 'unset' : '0 2px 4px rgba(0,0,0,0.5)',
    },
    '.vjs-chromecast-button > google-cast-launcher': {
      filter: isMobileDevice
        ? 'unset'
        : 'drop-shadow( 0px 2px 4px rgba(0, 0, 0, .5))',
    },
    '.vjs-time-control': {
      textShadow: isMobileDevice ? 'unset' : '0 2px 4px rgba(0,0,0,0.5)',
    },
  })

const seekToLiveStyle = (atLiveEdge: boolean) =>
  mq({
    '&&&.vjs-seek-to-live-control': {
      display: 'flex',
      alignItems: 'center',
      order: 31,
      outline: 0,
      '.vjs-icon-placeholder': {
        display: 'none',
      },
      '.vjs-seek-to-live-text': {
        cursor: 'pointer',
        margin: '8px',
        padding: '4px',
        lineHeight: '1em',
        boxShadow: ['unset', '0 2px 4px rgba(0,0,0,0.5)'],
        backgroundColor: atLiveEdge ? '#F00' : '#888',
        borderRadius: '2px',
      },
    },
  })
